'use client';

import { forwardRef } from 'react';
import { cn } from '@/core/utils/cn';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, hint, leftIcon, rightIcon, id, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const errorId = inputId ? `${inputId}-error` : undefined;
    const hintId = inputId ? `${inputId}-hint` : undefined;
    const describedBy = error ? errorId : hint ? hintId : undefined;
    return (
      <div className="space-y-1.5">
        {label && (
          <label htmlFor={inputId} className="block text-[12px] font-medium text-hms-textSub">
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-hms-textDim">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            className={cn(
              'w-full h-9 px-3 text-[13px] text-hms-text bg-hms-bg border border-hms-border rounded-lg',
              'placeholder:text-hms-textDim',
              'focus:outline-none focus:ring-2 focus:ring-hms-gold/30 focus:border-hms-gold/50',
              'transition-colors duration-150',
              leftIcon && 'pl-9',
              rightIcon && 'pr-9',
              error && 'border-hms-red/50 focus:ring-hms-red/30 focus:border-hms-red/50',
              props.disabled && 'opacity-50 cursor-not-allowed',
              className
            )}
            {...props}
          />
          {rightIcon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-hms-textDim">
              {rightIcon}
            </div>
          )}
        </div>
        {error && (
          <p id={errorId} role="alert" className="text-[11px] text-hms-red">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={hintId} className="text-[11px] text-hms-textDim">
            {hint}
          </p>
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';
